import React, { useState, useEffect, useCallback } from "react";
import { C } from "../constants";
import { IPD_COLORS } from "../../ipd/utils/ipdColors";
import IpdTextField from "../../ipd/utils/IpdTextField";
import IpdDateField from "../../ipd/utils/IpdDateField";
import IpdFormField from "../../ipd/utils/IpdFormField";
import IpdButton from "../../ipd/utils/IpdButton";
import IpdConfirmDialog from "../../ipd/utils/IpdConfirmDialog";
import SvgIcon from "../SvgIcon";
import { toast } from "react-toastify";
import { InputAdornment, MenuItem, Box, IconButton, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import IpdDataGrid from "../../ipd/utils/IpdDataGrid";
import { listPackages, deactivatePackage, copyPackage } from "../package-billing-api";
import { toBillingError } from "../billing-api";

const STATUS_TONE = {
  ACTIVE: { bg: IPD_COLORS.success.bg, color: IPD_COLORS.success.text },
  DRAFT: { bg: "#e0e7ff", color: "#4338ca" }, // indigo — no matching hue family in IPD_COLORS, left as-is (matches PackageAdminEditor.js precedent)
  INACTIVE: { bg: IPD_COLORS.pageBg, color: IPD_COLORS.textSecondary },
  RETIRED: { bg: IPD_COLORS.error.bg, color: IPD_COLORS.error.text },
};

const todayISO = () => new Date().toISOString().slice(0, 10);

function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function StatusBadge({ status }) {
  const tone = STATUS_TONE[status] || STATUS_TONE.INACTIVE;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        padding: "3px 9px",
        borderRadius: "999px",
        fontSize: "11px",
        fontWeight: 700,
        backgroundColor: tone.bg,
        color: tone.color,
      }}
    >
      {status || "—"}
    </Box>
  );
}

// ── Copy package modal ────────────────────────────────────────────────────────
function CopyPackageModal({ source, onClose, onCopied }) {
  const [code, setCode] = useState(`${source.code}-V2`);
  const [name, setName] = useState(`${source.name} (Copy)`);
  const [effectiveFrom, setEffectiveFrom] = useState(todayISO());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (!code.trim()) { setError("New package code is required"); return; }
    setSaving(true);
    setError("");
    try {
      const res = await copyPackage(source.id, {
        package_code: code.trim(),
        package_name: name.trim(),
        effective_from: effectiveFrom || undefined,
      });
      toast.success(`Copied to "${res.package.code}"`);
      onCopied(res.package.id);
    } catch (err) {
      const msg = toBillingError(err, "Could not copy package");
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "12px" } }}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "15px", fontWeight: 800, color: C.text }}>
        Copy package
        <IconButton onClick={onClose} size="small">
          <SvgIcon name="x" size={18} color={C.textSub} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        <Box sx={{ fontSize: "12px", color: C.textSub }}>
          Clones tariffs and components from <Box component="b" sx={{ color: C.text }}>{source.code}</Box> into a new draft package.
        </Box>
        <IpdFormField label="New package code">
          <IpdTextField fullWidth value={code} onChange={(e) => setCode(e.target.value)} />
        </IpdFormField>
        <IpdFormField label="New package name">
          <IpdTextField fullWidth value={name} onChange={(e) => setName(e.target.value)} />
        </IpdFormField>
        <IpdFormField label="Effective from">
          <IpdDateField value={effectiveFrom} onChange={(iso) => setEffectiveFrom(iso)} />
        </IpdFormField>
        {error && <Box sx={{ fontSize: "12px", color: C.danger, fontWeight: 600 }}>{error}</Box>}
      </DialogContent>
      <DialogActions sx={{ padding: "14px 20px", gap: "10px" }}>
        <IpdButton variant="outlined" onClick={onClose}>Cancel</IpdButton>
        <IpdButton onClick={submit} loading={saving}>Copy package</IpdButton>
      </DialogActions>
    </Dialog>
  );
}

// ── Package list ──────────────────────────────────────────────────────────────
export default function PackageAdminList({ onOpenEditor }) {
  const [packages, setPackages] = useState([]);
  const [statusFilter, setStatusFilter] = useState(""); // "" = all packages
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [copySource, setCopySource] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);

  const loadPackages = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setPackages(await listPackages({ status: statusFilter || undefined, activeOnly: false }));
    } catch (err) {
      setError(toBillingError(err, "Could not load packages"));
      setPackages([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { loadPackages(); }, [loadPackages]);

  const deactivate = async () => {
    const pkg = deactivateTarget;
    if (!pkg) return;
    setBusyId(pkg.id);
    try {
      await deactivatePackage(pkg.id);
      toast.success(`${pkg.code} deactivated`);
      setDeactivateTarget(null);
      await loadPackages();
    } catch (err) {
      toast.error(toBillingError(err, "Could not deactivate package"));
    } finally {
      setBusyId(null);
    }
  };

  const q = search.trim().toLowerCase();
  const visible = q
    ? packages.filter((p) => [p.code, p.name, p.category].filter(Boolean).join(" ").toLowerCase().includes(q))
    : packages;

  const columns = [
    { field: "code", headerName: "Code", flex: 1, minWidth: 130, sortable: false,
      renderCell: (p) => <Box component="span" sx={{ fontSize: "13px", fontWeight: 700, color: C.text, whiteSpace: "nowrap" }}>{p.value}</Box> },
    { field: "name", headerName: "Name", flex: 1.4, minWidth: 160, sortable: false,
      renderCell: (p) => <Box component="span" sx={{ fontSize: "13px", color: C.text }}>{p.value}</Box> },
    { field: "category", headerName: "Category", flex: 1, minWidth: 110, sortable: false,
      renderCell: (p) => <Box component="span" sx={{ fontSize: "12px", color: C.textSub }}>{p.value}</Box> },
    { field: "encounterType", headerName: "Encounter", flex: 1, minWidth: 100, sortable: false,
      renderCell: (p) => <Box component="span" sx={{ fontSize: "12px", color: C.textSub }}>{p.value}</Box> },
    { field: "status", headerName: "Status", flex: 1, minWidth: 110, sortable: false,
      renderCell: (p) => <StatusBadge status={p.value} /> },
    { field: "effectiveFrom", headerName: "Effective from", flex: 1, minWidth: 130, sortable: false,
      renderCell: (p) => <Box component="span" sx={{ fontSize: "13px", color: C.textSub, whiteSpace: "nowrap" }}>{fmtDate(p.value)}</Box> },
    { field: "actions", headerName: "", flex: 1.2, minWidth: 300, sortable: false, align: "right", headerAlign: "right",
      renderCell: (p) => (
        <Box sx={{ whiteSpace: "nowrap" }}>
          <IpdButton variant="outlined" size="small" onClick={() => onOpenEditor(p.row.id)}>
            Edit
          </IpdButton>
          <IpdButton variant="outlined" size="small" onClick={() => setCopySource(p.row)} sx={{ ml: "8px" }}>
            Copy
          </IpdButton>
          {p.row.active && (
            <IpdButton
              variant="outlined"
              size="small"
              onClick={() => setDeactivateTarget(p.row)}
              disabled={busyId === p.row.id}
              sx={{ ml: "8px" }}
            >
              Deactivate
            </IpdButton>
          )}
        </Box>
      ) },
  ];

  return (
    <Box sx={{ animation: "ipdFadeIn 0.2s ease" }}>
      {/* Toolbar */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "center", marginBottom: "18px" }}>
        <IpdTextField select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} SelectProps={{ displayEmpty: true }} sx={{ minWidth: "180px" }}>
          <MenuItem value="">All packages</MenuItem>
          <MenuItem value="DRAFT">Draft</MenuItem>
          <MenuItem value="ACTIVE">Active</MenuItem>
          <MenuItem value="INACTIVE">Inactive</MenuItem>
          <MenuItem value="RETIRED">Retired</MenuItem>
        </IpdTextField>
        <IpdTextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search packages by code / name…"
          sx={{ flex: 1, minWidth: "200px" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SvgIcon name="search" size={15} color={C.textSub} />
              </InputAdornment>
            ),
          }}
        />
        <IpdButton onClick={() => onOpenEditor(null)} startIcon={<SvgIcon name="plus" size={15} color={IPD_COLORS.white} />}>
          New package
        </IpdButton>
      </Box>

      {error && (
        <Box sx={{ marginBottom: "14px", padding: "10px 14px", backgroundColor: IPD_COLORS.error.bg, border: `1px solid ${IPD_COLORS.error.border}`, borderRadius: "8px", color: IPD_COLORS.error.text, fontSize: "13px" }}>
          {error}
        </Box>
      )}

      {/* Table — fixed list height so the pager anchors at the bottom of a full
          card instead of floating mid-page over the decorative background. */}
      <IpdDataGrid
        loading={loading}
        rows={visible}
        columns={columns}
        fillHeight
        emptyMessage="No packages yet. Create one to get started."
      />

      {copySource && (
        <CopyPackageModal
          source={copySource}
          onClose={() => setCopySource(null)}
          onCopied={(newId) => {
            setCopySource(null);
            loadPackages();
            onOpenEditor(newId);
          }}
        />
      )}

      <IpdConfirmDialog
        open={Boolean(deactivateTarget)}
        title={`Deactivate "${deactivateTarget?.code}"?`}
        description="Existing patient assignments are unaffected."
        destructive
        confirmLabel="Deactivate"
        loading={busyId === deactivateTarget?.id}
        onConfirm={deactivate}
        onCancel={() => setDeactivateTarget(null)}
      />
    </Box>
  );
}
